import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  InstagramDiscoveryCandidate,
  InstagramDiscoveryResponse,
} from '../../lib/workflowsApi';
import { workflowsAPI } from '../../lib/workflowsApi';

export type DiscoveryPhase = 'idle' | 'searching' | 'results' | 'importing' | 'imported' | 'failed';

/** Mirrors the backend's DISCOVERY_MAX_RESULTS product ceiling. */
export const DISCOVERY_MAX_RESULTS = 100;

/**
 * Mirrors the backend's published per-1k actor rates, used only for the offline
 * fallback estimate. Each active criterion that SCALES with `maxResults` starts
 * its OWN billed actor, so a local worst case is the SUM of those actors'
 * rates. `place` does NOT scale with `maxResults` and so contributes nothing
 * here — see `DISCOVERY_PLACE_FIXED_COST_USD` below, mirroring
 * `estimateRatePer1k` in instagram-discovery.service.ts.
 */
export const DISCOVERY_HASHTAG_RATE_PER_1K = 2.6;
export const DISCOVERY_SEARCH_RATE_PER_1K = 2.7;

/**
 * Mirrors the backend's `INSTAGRAM_PLACE_LOCATION_MATCH_LIMIT`
 * (apify-scraper.service.ts): a place search is now a SINGLE search-actor run
 * that bills one result per matched Instagram location, capped at this many
 * locations. One place NAME can resolve to several distinct locations, so
 * this is a cap on matched locations, never on `maxResults`.
 */
export const DISCOVERY_PLACE_LOCATION_MATCH_LIMIT = 8;

/**
 * `place` bills a FIXED charge — one `instagram_search` result per matched
 * location, up to the location cap — regardless of `maxResults` (see
 * `estimateCost`'s `placeCostUsd` in instagram-discovery.service.ts, which
 * adds this same flat amount straight into `estimatedCostUsd` rather than
 * folding it into `ratePer1k`, because it never scales with `maxResults`).
 * The offline fallback must add it too, or a place quote understates what
 * the backend actually charges.
 */
export const DISCOVERY_PLACE_FIXED_COST_USD =
  (DISCOVERY_PLACE_LOCATION_MATCH_LIMIT * DISCOVERY_SEARCH_RATE_PER_1K) / 1000;

const ESTIMATE_DEBOUNCE_MS = 300;

export type DiscoveryEstimate = InstagramDiscoveryResponse['estimate'];

export function clampMaxResults(requested: number): number {
  return Math.min(Math.max(Math.floor(requested) || 1, 1), DISCOVERY_MAX_RESULTS);
}

function estimateKeyFor(hashtags: string[], keyword: string, place: string, maxResults: number) {
  return JSON.stringify({ hashtags, keyword, place, maxResults });
}

export interface InstagramDiscoveryCriteriaInput {
  hashtags: string;
  keyword: string;
  place: string;
  maxResults: number;
}

const DEFAULT_CRITERIA: InstagramDiscoveryCriteriaInput = {
  hashtags: '',
  keyword: '',
  place: '',
  maxResults: 25,
};

/**
 * Splits the comma/space separated hashtag field into the array the API expects.
 * Accepts "#a, b  #c" because that is how people actually paste hashtags.
 */
export function parseHashtags(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((tag) => tag.replace(/^#/, '').trim())
    .filter(Boolean);
}

/**
 * FLOSEAI-635 — drives Instagram discovery from inside the Instagram workflow.
 *
 * Discovery is deliberately two steps: search returns candidates and creates
 * nothing, then the user picks which accounts are worth paying to enrich. The
 * import reuses the ordinary enrichment queue, so a discovered lead gets the
 * same treatment as a hand-pasted URL.
 */
export function useInstagramDiscovery(organizationId: string | undefined) {
  const [criteria, setCriteria] = useState<InstagramDiscoveryCriteriaInput>(DEFAULT_CRITERIA);
  const [phase, setPhase] = useState<DiscoveryPhase>('idle');
  const [result, setResult] = useState<InstagramDiscoveryResponse | null>(null);
  // Snapshot of the place the running/last-completed search actually used.
  // The place input stays editable while results show (a failed search must
  // stay retryable), so result-time copy that names the place must read this
  // instead of the live, still-editable `criteria.place` (FLOSEAI-635 Codex
  // review of #1660).
  const [resultPlace, setResultPlace] = useState('');
  const [resultKey, setResultKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState(0);

  const hashtags = useMemo(() => parseHashtags(criteria.hashtags), [criteria.hashtags]);

  const hasCriteria =
    hashtags.length > 0 || criteria.keyword.trim().length > 0 || criteria.place.trim().length > 0;

  const updateCriteria = useCallback((patch: Partial<InstagramDiscoveryCriteriaInput>) => {
    setCriteria((current) => {
      const next = { ...current, ...patch };
      if (patch.maxResults !== undefined) {
        next.maxResults = clampMaxResults(patch.maxResults);
      }
      return next;
    });
  }, []);

  // The quote remembers WHICH criteria it was fetched for. Only a quote whose
  // snapshot matches the committed criteria counts as the pre-spend disclosure —
  // a stale or in-flight one must gate the paid search, not decorate it.
  const [quote, setQuote] = useState<{ key: string; data: DiscoveryEstimate } | null>(null);
  const keyword = criteria.keyword.trim();
  const place = criteria.place.trim();
  const { maxResults } = criteria;

  const committedKey = estimateKeyFor(hashtags, keyword, place, maxResults);
  const estimate = quote !== null && quote.key === committedKey ? quote.data : null;
  const estimateReady = estimate !== null;

  useEffect(() => {
    if (phase !== 'results' || resultKey === null || resultKey === committedKey) return;
    setPhase('idle');
    setResult(null);
    setSelected(new Set());
    setResultKey(null);
  }, [phase, resultKey, committedKey]);

  useEffect(() => {
    if (!organizationId) {
      setQuote(null);
      return;
    }

    const key = estimateKeyFor(hashtags, keyword, place, maxResults);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await workflowsAPI.estimateInstagramDiscovery({
          organizationId,
          maxResults,
          ...(hashtags.length > 0 && { hashtags }),
          ...(keyword && { keyword }),
          ...(place && { place }),
        });
        if (!cancelled) setQuote({ key, data });
      } catch {
        if (cancelled) return;
        // Mirrors `estimateRatePer1k` on the backend: only criteria that SCALE
        // with `maxResults` (hashtag, keyword) contribute to the rate. `place`
        // is a typed criterion whose rate is honestly 0 — it must NOT fall
        // through the `||` below, or a place-only search would silently be
        // re-priced at the search rate and scaled by `maxResults` again,
        // exactly the bug this ticket fixes.
        const scalingRatePer1k =
          (hashtags.length > 0 ? DISCOVERY_HASHTAG_RATE_PER_1K : 0) +
          (keyword ? DISCOVERY_SEARCH_RATE_PER_1K : 0);
        const anyCriteriaTyped = hashtags.length > 0 || Boolean(keyword) || Boolean(place);
        const ratePer1k =
          scalingRatePer1k > 0
            ? scalingRatePer1k
            : anyCriteriaTyped
              ? 0
              : DISCOVERY_SEARCH_RATE_PER_1K;
        // Mirrors `estimateCost`'s split on the backend: the fixed place charge
        // does not scale with maxResults, so it is added on top of the
        // per-result total instead of being folded into `ratePer1k`.
        const placeFixedCostUsd = place ? DISCOVERY_PLACE_FIXED_COST_USD : 0;
        setQuote({
          key,
          data: {
            maxResults,
            estimatedCostUsd: Number(
              ((maxResults * ratePer1k) / 1000 + placeFixedCostUsd).toFixed(4),
            ),
            ratePer1k,
            limitedByPlan: false,
          },
        });
      }
    }, ESTIMATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, hashtags, keyword, place, maxResults]);

  const toggleCandidate = useCallback((username: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(username)) {
        next.delete(username);
      } else {
        next.add(username);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(result?.candidates.map((c) => c.username) ?? []));
  }, [result]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const search = useCallback(
    async (maxResultsOverride?: number) => {
      if (!organizationId || !hasCriteria) return;

      const effectiveMaxResults =
        maxResultsOverride !== undefined
          ? clampMaxResults(maxResultsOverride)
          : criteria.maxResults;
      if (effectiveMaxResults !== criteria.maxResults) {
        updateCriteria({ maxResults: effectiveMaxResults });
      }

      // Spending is only allowed behind a quote that describes THIS submission.
      // Committing the limit above makes the debounced effect fetch the missing
      // quote, so the refusal is temporary by construction.
      if (quote?.key !== estimateKeyFor(hashtags, keyword, place, effectiveMaxResults)) return;

      setPhase('searching');
      setError(null);
      setSelected(new Set());
      setResultPlace(place);
      setResultKey(estimateKeyFor(hashtags, keyword, place, effectiveMaxResults));

      try {
        const { data } = await workflowsAPI.discoverInstagramLeads({
          organizationId,
          ...(hashtags.length > 0 && { hashtags }),
          ...(criteria.keyword.trim() && { keyword: criteria.keyword.trim() }),
          ...(criteria.place.trim() && { place: criteria.place.trim() }),
          maxResults: effectiveMaxResults,
        });

        setResult(data);
        // Nothing is pre-selected: every ticked account is paid enrichment, so
        // spending is opt-in per account (with select-all one tap away).
        setSelected(new Set());
        setPhase('results');
      } catch (caught) {
        setError(resolveApiErrorMessage(caught));
        setPhase('failed');
      }
    },
    [
      organizationId,
      hasCriteria,
      hashtags,
      criteria.keyword,
      criteria.place,
      criteria.maxResults,
      updateCriteria,
      quote,
      keyword,
      place,
    ],
  );

  const importSelected = useCallback(
    async (segmentName: string) => {
      if (!organizationId || selected.size === 0) return;

      setPhase('importing');
      setError(null);

      try {
        const { data } = await workflowsAPI.importInstagramCandidates({
          organizationId,
          usernames: [...selected],
          segmentName: segmentName.trim() || 'Instagram discovery',
        });

        setImportedCount(data.enqueued);
        setPhase('imported');
      } catch (caught) {
        setError(resolveApiErrorMessage(caught));
        setPhase('failed');
      }
    },
    [organizationId, selected],
  );

  const reset = useCallback(() => {
    setCriteria(DEFAULT_CRITERIA);
    setPhase('idle');
    setResult(null);
    setSelected(new Set());
    setResultKey(null);
    setError(null);
    setImportedCount(0);
  }, []);

  /**
   * Unlike `reset`, keeps the typed criteria — a failed search
   * (`emptyReason: 'search_failed'`) still lands in the `results` phase, so
   * the way back to a clean form must not throw away what the user already
   * typed (FLOSEAI-635 T13).
   */
  const retry = useCallback(() => {
    setPhase('idle');
    setResult(null);
    setSelected(new Set());
    setResultKey(null);
    setError(null);
  }, []);

  return {
    criteria,
    updateCriteria,
    estimate,
    estimateReady,
    hashtags,
    hasCriteria,
    phase,
    result,
    resultPlace,
    candidates: result?.candidates ?? ([] as InstagramDiscoveryCandidate[]),
    selected,
    selectedCount: selected.size,
    toggleCandidate,
    selectAll,
    clearSelection,
    search,
    importSelected,
    importedCount,
    error,
    reset,
    retry,
  };
}

export function resolveApiErrorMessage(caught: unknown): string {
  const response = (caught as { response?: { data?: { message?: string | string[] } } })?.response;
  const message = response?.data?.message;
  if (Array.isArray(message)) return message[0];
  if (typeof message === 'string') return message;
  return caught instanceof Error ? caught.message : 'Unknown error';
}
